import {openConfirmDialog, useDialogCloseGuard} from 'platform/components';

import {useRef} from 'react';

import i18n from '@dms/i18n';

import {type RequiredTestIdProps, suffixTestId} from 'shared/web';

type FinancingEndingComposeCloseGuardProps = RequiredTestIdProps & {
  /** True while the e-mail is being sent; the dialog then stays open without asking. */
  isLocked: () => boolean;
  /** Read when the dialog is about to close, so it sees the values typed last. */
  hasUnsavedChanges: () => boolean;
};

/**
 * The unsaved-message confirm of `UnsavedMessageCloseGuard`, which also keeps the dialog open while
 * the send runs. A dialog takes one close guard, so the two cannot be stacked.
 */
export function FinancingEndingComposeCloseGuard(props: FinancingEndingComposeCloseGuardProps) {
  const isConfirmOpenRef = useRef(false);

  useDialogCloseGuard((close) => {
    // Escape pressed on the confirm reaches this dialog as well; the confirm answers it.
    if (props.isLocked() || isConfirmOpenRef.current) {
      return true;
    }
    if (!props.hasUnsavedChanges()) {
      return false;
    }

    isConfirmOpenRef.current = true;
    openConfirmDialog({
      text: i18n.t('general.notifications.unsavedChanges.description'),
      onConfirm: close,
      onCloseComplete: () => {
        isConfirmOpenRef.current = false;
      },
      'data-testid': suffixTestId('unsavedChanges', props),
    });
    return true;
  });

  return null;
}
